/** Parse `Content-Disposition` filename, preferring RFC 5987 `filename*`. */
export const filenameFromContentDisposition = (
  header: string | null | undefined,
  fallback: string,
): string => {
  if (!header) return fallback;

  const utf8Match = (/filename\*\s*=\s*(?:UTF-8'')?(?<name>[^;]+)/i).exec(header);
  if (utf8Match?.groups?.name) {
    const raw = utf8Match.groups.name.trim().replace(/^"|"$/g, "");
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }

  const plainMatch =
    (/filename\s*=\s*"(?<quoted>[^"]+)"|filename\s*=\s*(?<bare>[^;]+)/i).exec(header);
  const plainName = plainMatch?.groups?.quoted || plainMatch?.groups?.bare;
  if (plainName) {
    return plainName.trim();
  }

  return fallback;
};

export const triggerBlobDownload = (blob: Blob, fileName: string): void => {
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.style.visibility = "hidden";
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(link.href);
};

type DownloadAuthOpts = {
  headers?: HeadersInit;
  fallbackFilename: string;
};

/**
 * Fetch a same-origin (or CORS) file and save it using Content-Disposition when present.
 * Returns the filename used for the download.
 */
export const downloadAuthenticatedFile = async (
  url: string,
  { headers, fallbackFilename }: DownloadAuthOpts,
): Promise<string> => {
  const res = await fetch(url, { headers });

  if (res.status === 404) {
    throw new Error("not_found");
  }
  if (!res.ok) {
    throw new Error("download_failed");
  }

  const blob = await res.blob();
  const fileName = filenameFromContentDisposition(
    res.headers.get("content-disposition"),
    fallbackFilename,
  );
  triggerBlobDownload(blob, fileName);
  return fileName;
};
